import React, { PropsWithChildren, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';

import AliceCarousel from 'react-alice-carousel';

import { BaseStyleEnums } from '@nhl/nhl-breakaway-ui';

import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';

import { CarouselContext } from './CarouselContextProvider';
import './alice-carousel-styles.css';
import { CarouselSettings, SlideData } from './types';
import { computeVisibleSlideData } from './utils';

import { BaseCarouselContainer, CarouselWrapper } from './styles';

export type Props<T> = {
    height: number;
    leftPadding?: number;
    overrideAnimation?: boolean;
    regularSlideWidth: number;
    renderNextButton: (slideNext: () => void) => ReactNode;
    renderPrevButton: (slidePrev: () => void) => ReactNode;
    rightPadding?: number;
    shouldExpandBelowMediumScreens?: boolean;
    slideData: SlideData<T>;
    skinnySlideWidth: number;
    stickySlide?: ReactNode;
    stickySlideWidth?: number;
};

const BaseCarousel = <T,>({
    height,
    leftPadding = 0,
    overrideAnimation = false,
    regularSlideWidth,
    renderNextButton,
    renderPrevButton,
    rightPadding = 0,
    shouldExpandBelowMediumScreens = false,
    slideData,
    skinnySlideWidth,
    stickySlide,
    stickySlideWidth = 0,
}: PropsWithChildren<Props<T>>) => {
    const [carouselWidth, setCarouselWidth] = useState<number>(0);

    const carouselContext = useContext(CarouselContext);

    const screenSize = useSizeWithThrottle();

    const wrapperContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (wrapperContainerRef.current) {
            const paddingAccommodation =
                leftPadding + rightPadding + (stickySlide !== undefined ? stickySlideWidth : 0);

            if (screenSize.width > BaseStyleEnums.ScreenWidthBreakpoints.medium) {
                setCarouselWidth(wrapperContainerRef.current.getBoundingClientRect().width - paddingAccommodation);
            } else {
                setCarouselWidth(screenSize.width - paddingAccommodation);
            }
        }
    }, [screenSize, leftPadding, rightPadding, stickySlide, stickySlideWidth]);

    useEffect(() => {
        carouselContext.setShouldShowControls(screenSize.width > BaseStyleEnums.ScreenWidthBreakpoints.medium);
    }, [carouselContext, screenSize]);

    const carouselSettings = useMemo(
        (): CarouselSettings => ({
            mouseTracking: !carouselContext.shouldShowControls,
            touchTracking: !carouselContext.shouldShowControls,
        }),
        [carouselContext.shouldShowControls]
    );

    useEffect(() => {
        const availableSpace = carouselWidth;

        let visibleSlideData = computeVisibleSlideData(
            slideData.slideMetadata.slice(carouselContext.activeIndex),
            availableSpace,
            skinnySlideWidth,
            regularSlideWidth
        );

        if (visibleSlideData.widthOfCurrentlyVisibleSlides < availableSpace) {
            visibleSlideData = computeVisibleSlideData(
                slideData.slideMetadata.slice(0, carouselContext.activeIndex).reverse(),
                availableSpace,
                skinnySlideWidth,
                regularSlideWidth,
                visibleSlideData
            );
        }

        carouselContext.setVisibleSlides(visibleSlideData.slidesShowing);
    }, [carouselContext, carouselWidth, regularSlideWidth, skinnySlideWidth, screenSize, slideData]);

    const slidePrev = () => {
        carouselContext.setActiveIndex(Math.max(0, carouselContext.activeIndex - carouselContext.visibleSlides - 1));
        carouselContext.setHasUserChangedIndex(true);
    };

    const slideNext = () => {
        const potentialNextIndex = carouselContext.activeIndex + carouselContext.visibleSlides - 1;
        const maxIndex = slideData.slides.length - 1;

        if (potentialNextIndex > maxIndex) {
            carouselContext.setActiveIndex(maxIndex);
        } else {
            carouselContext.setActiveIndex(potentialNextIndex);
        }

        carouselContext.setHasUserChangedIndex(true);
    };

    return (
        <>
            {renderPrevButton(slidePrev)}
            <BaseCarouselContainer ref={wrapperContainerRef}>
                {stickySlide}
                <CarouselWrapper
                    $width={carouselWidth}
                    $height={height}
                    $overrideAnimation={carouselContext.shouldShowControls || overrideAnimation}
                    $shouldExpandBelowMediumScreens={shouldExpandBelowMediumScreens}
                >
                    <AliceCarousel
                        autoWidth
                        disableButtonsControls
                        disableDotsControls
                        responsive={{
                            [carouselWidth]: { items: carouselContext.visibleSlides },
                        }}
                        activeIndex={carouselContext.activeIndex}
                        items={slideData.slides}
                        onSlideChanged={({ item }) => {
                            // NB: This is only called on mobile devices that don't use our custom controls
                            carouselContext.setActiveIndex(item);
                            carouselContext.setHasUserChangedIndex(true);
                        }}
                        {...carouselSettings}
                        paddingRight={rightPadding}
                        paddingLeft={leftPadding}
                    />
                </CarouselWrapper>
            </BaseCarouselContainer>
            {renderNextButton(slideNext)}
        </>
    );
};

export default BaseCarousel;
